import React from 'react';

interface DisbursementLoanItem {
    id: number;
    bank: string;
    digit: string;
    value: number;
    agency: string;
    status: string;
    account: string;
    loan_id: number;
    created_at: string;
    external_id: string;
    modified_at: string;
    url_receipt: string;
    account_type: string;
    identificator: string;
    transfer_date: string;
    authentication: string | null;
    control_number: string;
    error_messages: string | null;
    payment_method: number;
    transaction_id: string;
    bank_account_id: number;
    operation_number: string | null;
    disbursement_type: string;
    file_name_receipt: string;
}

interface Props {
    disbursements: DisbursementLoanItem[];
}

const statusTranslation: Record<string, string> = {
    'TRANSFER_SUCCESS': 'Transferência Concluída',
    'TRANSFER_PENDING': 'Transferência Pendente',
    'TRANSFER_FAILED': 'Transferência Falhou',
    'CANCELLED': 'Cancelado'
};

const disbursementTypeTranslation: Record<string, string> = {
    'TRANSFER_ORDER': 'Ordem de Transferência',
    'PIX': 'PIX',
    'TED': 'TED',
    'DOC': 'DOC'
};

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(value);
};

const formatDate = (dateStr: string): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
};

const DisbursementLoanManager: React.FC<Props> = ({ disbursements }) => {
    if (!disbursements || disbursements.length === 0) {
        return (
            <div className="mt-6 p-4 bg-gray-50 dark:bg-slate-700 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Desembolsos
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Nenhum desembolso registrado
                </p>
            </div>
        );
    }

    return (
        <div className="mt-6 p-4 bg-gray-50 dark:bg-slate-700 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Desembolsos ({disbursements.length})
            </h3>
            
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                    <thead className="bg-gray-100 dark:bg-slate-800">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                Identificador
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                Valor
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                Data Transferência
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                Banco
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                Agência/Conta
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                Tipo
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                Comprovante
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-gray-700">
                        {disbursements.map((disbursement) => (
                            <tr key={disbursement.id} className="hover:bg-gray-50 dark:hover:bg-slate-800">
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                    {disbursement.identificator || '-'}
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {formatCurrency(disbursement.value)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                    {formatDate(disbursement.transfer_date)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                    {disbursement.bank}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                    {disbursement.agency}/{disbursement.account}-{disbursement.digit}
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {disbursement.account_type}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                    {disbursementTypeTranslation[disbursement.disbursement_type] || disbursement.disbursement_type}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                        disbursement.status === 'TRANSFER_SUCCESS'
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                            : disbursement.status === 'TRANSFER_PENDING'
                                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                    }`}>
                                        {statusTranslation[disbursement.status] || disbursement.status}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    {disbursement.url_receipt ? (
                                        <a
                                            href={disbursement.url_receipt}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 dark:text-blue-400 hover:underline"
                                        >
                                            Ver Comprovante
                                        </a>
                                    ) : (
                                        <span className="text-gray-400 dark:text-gray-500">-</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Resumo */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Total Desembolsado:
                    </span>
                    <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                        {formatCurrency(disbursements.reduce((sum, d) => sum + d.value, 0))}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default DisbursementLoanManager;
